import React from 'react';
import styled from '@emotion/styled';
import { mqUser } from 'utils/responsive';

interface PaginationProps {
    currentPage: number;
    hasNextPage: boolean;
    onPreviousPage?: () => void;
    onNextPage?: () => void;
    onPageChange?: (page: number) => void;
}

const PaginationWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px 0;
    margin-top: 20px;
    width: 100%;
`;

const ArrowButton = styled.button<{ disabled?: boolean }>`
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    color: ${(props) => (props.disabled ? '#e0e0e0' : '#666666')};
    font-size: 20px;
    font-weight: 500;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};

    &:hover:not(:disabled) {
        color: var(--main-color-blue);
        transform: translateY(-1px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    ${mqUser('SP')} {
        width: 36px;
        height: 36px;
        font-size: 18px;
    }
`;

const PageButton = styled.button<{ isActive?: boolean }>`
    min-width: 40px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid ${(props) => (props.isActive ? 'var(--main-color-blue)' : '#e0e0e0')};
    border-radius: 8px;
    background-color: ${(props) => (props.isActive ? 'var(--main-color-blue)' : '#ffffff')};
    color: ${(props) => (props.isActive ? '#ffffff' : 'var(--dark-blue)')};
    font-size: var(--font-medium);
    font-weight: ${(props) => (props.isActive ? '700' : '500')};
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${(props) => (props.isActive ? 'var(--main-color-blue)' : '#f5f5f5')};
        border-color: ${(props) => (props.isActive ? 'var(--main-color-blue)' : 'var(--main-color-blue)')};
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    ${mqUser('SP')} {
        min-width: 36px;
        height: 36px;
        padding: 0 10px;
        font-size: var(--font-small);
    }
`;

const Ellipsis = styled.span`
    color: var(--dark-blue);
    font-size: var(--font-medium);
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    hasNextPage,
    onPreviousPage,
    onNextPage,
    onPageChange,
}) => {
    const isFirstPage = currentPage === 1;
    const previousPage = currentPage - 1;
    const showPreviousPage = currentPage > 1;

    const handlePageClick = (page: number) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    return (
        <PaginationWrapper>
            <ArrowButton disabled={isFirstPage} onClick={onPreviousPage}>
                ‹
            </ArrowButton>
            {showPreviousPage && (
                <>
                    <PageButton isActive={false} onClick={() => handlePageClick(previousPage)}>
                        {previousPage}
                    </PageButton>
                </>
            )}
            <PageButton isActive={true}>{currentPage}</PageButton>
            <Ellipsis>...</Ellipsis>
            <ArrowButton disabled={!hasNextPage} onClick={onNextPage}>
                ›
            </ArrowButton>
        </PaginationWrapper>
    );
};
